import { useEffect, useState, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Skeleton,
    Box,
    Typography,
} from '@mui/material';
import { getFixcenterProductByCategoryId, getProductsPricingDetails } from '../../services/products.service';
import { IProductPricingDetail } from '../../shared/models/product-pricing-detail.interface';
import { useParams } from 'react-router-dom';
import { FixcenterProduct } from '../../shared/models/fixcenter-product.model';
import { IFixcenterProductDto } from '../../shared/models/fixcenter-product-dto.interface';
import { useTranslation } from '../../i18n/useTranslation';
import { Utils } from '../../shared/constants/utilities.constants';
import { claimIsActive } from '../../shared/services/claims.service';
import MarkAsFavoriteControl from '../../containers/favorite-products/mark-as-favorite/mark-as-favorite-control';
import { ErpClaimCode } from '../../shared/models/erp-claim-code.enum';
import { getCurrencySymbol } from '../order/services/currency.service';
import { useBetween } from 'use-between';
import { PermissionsUseBetween } from '../../shared/use-between/permissions-use-between';
import {
    calculateTotalPricePerProduct,
    getNetPriceWithPrecision,
    getPriceWithPrecision,
} from '../shopping-cart/services/shopping-cart.service';
import QuantityInput from '../../shared/input/quantity-input ';
import AddToShoppingCart from './add-to-shopping-cart';
import styles from './FixcenterOrderingTable.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    determineIfRedirectOrDownload,
    getFileByUuid,
    getFileMapByUuids,
} from '../../shared/services/attachments.service';
import { PDF_EXTENSION, RFA_EXTENSION } from '../../shared/constants/file-extension.constants';
import { AttachmentFile } from '../../shared/models/attachment-file.model';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import OrderingTableFilters from './OrderingTableFilters/OrderingTableFilters';
import OrderingProductsTableSkeleton from './OrderingProductsTableSkeleton';
import { IFeature } from '../product-filters-menu/interfaces/feature.interface';
import { IFeatureDiameterDto } from '../product-filters-menu/interfaces/feature-diameter-dto.interface';
import { ProductFiltersUseBetween } from '../product-filters-menu/use-between/product-filters-use-between';
import { formatFeatureValue } from '../../shared/services/feature-value-formatter.service';
import { productMatchesFeatureFilters } from '../../shared/services/product-feature-filter.service';
import { ProductFeatureDetailsDto } from '../../shared/models/product-feature-details-dto.model';

interface QuantityState {
    [productId: string]: number;
}

interface WarningState {
    [productId: string]: string;
}

export default function FixcenterOrderingTable(props: any) {
    const [products, setProducts] = useState<FixcenterProduct[]>([]);
    const [pricing, setPricing] = useState<Record<string, IProductPricingDetail>>({});
    const [visibleCount, setVisibleCount] = useState(20);
    const [loading, setLoading] = useState(false);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const { categoryId } = useParams();
    const { productId } = useParams();
    const { t } = useTranslation('translation');
    const { claims } = useBetween(PermissionsUseBetween);
    const [quantities, setQuantities] = useState<QuantityState>({});
    const [warnings, setWarnings] = useState<WarningState>({});
    const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
    const [familyCode, setFamilyCode] = useState<string>('');
    const [imageFileId, setImageFileId] = useState<string | null>(null);
    const [filterFeatures, setFilterFeatures] = useState<IFeature[]>([]);
    const [diameterFilter, setDiameterFilter] = useState<IFeatureDiameterDto>();
    const [variantFeatureValues, setVariantFeatureValues] = useState<Record<string, ProductFeatureDetailsDto[]>>({});
    const [productsLoaded, setProductsLoaded] = useState(false);
    const [filtersLoaded, setFiltersLoaded] = useState(false);
    const { featuresFilters } = useBetween(ProductFiltersUseBetween);

    // Waits for both products and filters so the header never gains columns after first paint. Filters
    // only resolve when productId/categoryId are present.
    const isTableReady = productsLoaded && (filtersLoaded || !productId || !categoryId);

    const diameterColumnFeatures: IFeature[] = diameterFilter
        ? [diameterFilter.mm, diameterFilter.dn, diameterFilter.nps].filter(Boolean)
        : [];
    const columnFeatures: IFeature[] = [...filterFeatures, ...diameterColumnFeatures];

    const filteredProducts = products.filter((product) =>
        productMatchesFeatureFilters(variantFeatureValues[product.id], featuresFilters, columnFeatures)
    );

    const handleFiltersLoaded = (
        features: IFeature[],
        loadedVariantFeatureValues: Record<string, ProductFeatureDetailsDto[]>,
        loadedDiameterFilter?: IFeatureDiameterDto
    ) => {
        setFilterFeatures(features);
        setDiameterFilter(loadedDiameterFilter);
        setVariantFeatureValues(loadedVariantFeatureValues);
        setFiltersLoaded(true);
    };

    const getFeatureCellValue = (product: FixcenterProduct, feature: IFeature): string => {
        const detail = variantFeatureValues[product.id]?.find((d) => d.featureCode === feature.code);
        return formatFeatureValue(detail?.value, feature.labelInEnglish);
    };

    // 8 fixed header columns (expand icon, name, code, price, documents, packaging, min quantity, actions),
    // plus one per feature column, plus 2 more when CUSTOMER_VIEWP_ROLE columns are shown.
    const totalColumnCount =
        8 + columnFeatures.length + (claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) ? 2 : 0);

    useEffect(() => {
        (async () => {
            const response = await getFixcenterProductByCategoryId(productId as string, categoryId as string);
            const dto: IFixcenterProductDto = response.data;
            setQuantities(
                Object.fromEntries(dto.data.map((product: FixcenterProduct) => [product.id, getMinQuantity(product)]))
            );
            setWarnings(Object.fromEntries(dto.data.map((product: FixcenterProduct) => [product.id, ''])));
            setProducts(dto.data);
            props.handleSetProducts(dto.data);
            props.handleSetViewKeyValueProducts(dto.viewItemKeyValue);
            setProductsLoaded(true);
        })();
    }, []);

    useEffect(() => {
        if (props?.product) {
            const { product } = props;
            setFamilyCode(product.name);
            setImageFileId(product.imageIds?.[0] ?? null);
        }
    }, [props.product]);

    const loadPrices = async (start: number, end: number) => {
        const batch = filteredProducts.slice(start, end);
        if (batch.length === 0) return;

        const uuids = batch.map((p) => p.id);

        const batchSize = 5;
        const chunks = [];
        for (let i = 0; i < uuids.length; i += batchSize) {
            chunks.push(uuids.slice(i, i + batchSize));
        }

        await Promise.all(
            chunks.map(async (chunk) => {
                const response = await getProductsPricingDetails({ productUuids: chunk });
                setPricing((prev) => ({ ...prev, ...response.data }));
            })
        );
    };

    useEffect(() => {
        if (products.length > 0) {
            setLoading(true);
            loadPrices(0, visibleCount).finally(() => setLoading(false));
        }
    }, [products]);

    useEffect(() => {
        if (featuresFilters.length === 0 && products.length === filteredProducts.length) {
            return;
        }
        setVisibleCount(20);
        setLoading(true);
        loadPrices(0, Math.min(20, filteredProducts.length)).finally(() => setLoading(false));
    }, [featuresFilters]);

    useEffect(() => {
        if (!filteredProducts.length) return;

        let isFetching = false;

        let scrollTimeout: NodeJS.Timeout | null = null;

        const handleScroll = () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const container = document.getElementById('AppContainer');
                if (!container || isFetching) return;

                const scrollPosition = container.scrollTop + container.clientHeight;
                const triggerPoint = container.scrollHeight * 0.85;

                if (scrollPosition >= triggerPoint && visibleCount < filteredProducts.length) {
                    isFetching = true;
                    const newCount = Math.min(visibleCount + 20, filteredProducts.length);
                    setVisibleCount(newCount);
                    loadPrices(visibleCount, newCount).finally(() => {
                        isFetching = false;
                    });
                }
            }, 100);
        };

        const container = document.getElementById('AppContainer');
        container?.addEventListener('scroll', handleScroll);

        return () => container?.removeEventListener('scroll', handleScroll);
    }, [visibleCount, filteredProducts]);

    const getMinQuantity = (product: FixcenterProduct): number => {
        return !Utils.isNullOrUndefined(product.salePackagingUnits) && product.salePackagingUnits.length > 0
            ? product.salePackagingUnits[0].minQty
            : 1;
    };

    const getOrderQuantity = (product: FixcenterProduct): number => {
        const orderQty =
            !Utils.isNullOrUndefined(product.salePackagingUnits) && product.salePackagingUnits.length > 0
                ? product.salePackagingUnits[0].orderQty
                : 1;
        return orderQty > 0 ? orderQty : 1;
    };

    const calculateUnitPrice = (productPrice: IProductPricingDetail): number => {
        return productPrice.priceFromPricing * (1 - productPrice.discount);
    };

    const handleQuantityChange = (newQty: number, product: FixcenterProduct) => {
        const minQuantity: number = getMinQuantity(product);
        if (newQty < minQuantity) {
            setWarnings((prevWarnings) => ({
                ...prevWarnings,
                [product.id]: 'commonErrors.MIN_QUANTITY',
            }));
        } else if (newQty % minQuantity) {
            setWarnings((prevWarnings) => ({
                ...prevWarnings,
                [product.id]: 'commonErrors.NOT_MATCHING_PACKAKING_SIZE',
            }));
        } else {
            setWarnings((prevWarnings) => ({
                ...prevWarnings,
                [product.id]: '',
            }));
        }
        setQuantities((prevQuantities) => ({
            ...prevQuantities,
            [product.id]: newQty,
        }));
    };

    const fetchFileMap = async (product: FixcenterProduct) => {
        if (Array.isArray(product.attachments)) {
            toggleRowExpansion(product.id);
            return;
        }
        if (Array.isArray(product.attachmentIds) && product.attachmentIds.length > 0) {
            getFileMapByUuids(product.attachmentIds).then((response: any) => {
                if (response) {
                    const _fileMap = response.data as Record<string, string>;
                    if (!Array.isArray(product.attachments)) {
                        product.attachments = [];
                    }
                    for (const [key, value] of Object.entries(_fileMap)) {
                        if (value.endsWith(RFA_EXTENSION) || value.endsWith(PDF_EXTENSION)) {
                            product.attachments.push({ id: key, name: value } as AttachmentFile);
                        }
                    }
                    toggleRowExpansion(product.id);
                }
            });
        }
    };

    const getFileDocument = (fileDocumentUuid: string) => {
        getFileByUuid(fileDocumentUuid as string).then((response) => {
            if (response) {
                const model: AttachmentFile = response.data;
                if (model) {
                    determineIfRedirectOrDownload(model);
                }
            }
        });
    };

    const openDocuments = (product: FixcenterProduct) => {
        fetchFileMap(product);
    };

    const toggleRowExpansion = (productUuid: string) => {
        setExpandedRows((prevState) => ({
            ...prevState,
            [productUuid]: !prevState[productUuid],
        }));
    };

    return (
        <div>
            {productId && categoryId && (
                <OrderingTableFilters
                    productId={productId}
                    categoryId={categoryId}
                    onFiltersLoaded={handleFiltersLoaded}
                />
            )}
            <Typography variant="body2" sx={{ mt: 3, mb: 3, fontWeight: 'bold', color: '#000' }}>
                {t('myAccountInfo.orders.MESSAGE_1')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, fontWeight: 'bold', color: '#000' }}>
                {t('myAccountInfo.orders.MESSAGE_2')}
            </Typography>
            {!isTableReady ? (
                <OrderingProductsTableSkeleton />
            ) : (
                <Box sx={{ display: 'flex' }}>
                    <TableContainer
                        sx={{
                            marginTop: '1rem',
                            width: '100%',
                        }}
                    >
                        <Table
                            sx={{
                                width: '100%',
                                tableLayout: 'auto',
                                '& .MuiTableCell-root': {
                                    fontSize: '0.8rem',
                                    padding: '6px 8px',
                                    overflowWrap: 'break-word',
                                },
                                '& .MuiTableCell-head': { fontSize: '0.75rem', padding: '6px 8px' },
                            }}
                            stickyHeader
                            className={styles.table_container}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center"></TableCell>
                                    <TableCell>
                                        <b>{t('VARIANT_NAME')}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{t('CODE')}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{t('DOCUMENTS')}</b>
                                    </TableCell>
                                    {columnFeatures.map((feature) => (
                                        <TableCell key={feature.id}>
                                            <b>{feature.labelInEnglish ?? feature.name}</b>
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <b>{t('PACKAGING')}</b>
                                    </TableCell>
                                    <TableCell>
                                        <b>{t('MIN_QUANTITY')}</b>
                                    </TableCell>
                                    {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                                        <TableCell>
                                            <b>{t('NET_PRICE')}</b>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <b>{t('QUANTITY')}</b>
                                    </TableCell>
                                    {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                                        <TableCell>
                                            <b>{t('TOTAL_PRICE')}</b>
                                        </TableCell>
                                    )}
                                    <TableCell align="center"></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredProducts.slice(0, visibleCount).map((product) => {
                                    const price = pricing[product.id];
                                    const isLoaded = !!price;

                                    return (
                                        <React.Fragment key={product.id}>
                                            <TableRow hover>
                                                <TableCell align="center">
                                                    <MarkAsFavoriteControl
                                                        categoryUuid={categoryId}
                                                        parentProductUuid={productId}
                                                        variantProductUuid={product.id}
                                                        isActiveInit={product.isFavorite}
                                                    ></MarkAsFavoriteControl>
                                                </TableCell>
                                                <TableCell>{product.translateName ?? product.name}</TableCell>
                                                <TableCell>{product.code}</TableCell>
                                                <TableCell align="center">
                                                    {product.attachmentIds && product.attachmentIds?.length > 0 && (
                                                        <FontAwesomeIcon
                                                            className={styles.redIcon}
                                                            size="2x"
                                                            onClick={() => {
                                                                openDocuments(product);
                                                            }}
                                                            icon={faFile}
                                                        />
                                                    )}
                                                </TableCell>
                                                {columnFeatures.map((feature) => (
                                                    <TableCell key={feature.id}>
                                                        {getFeatureCellValue(product, feature)}
                                                    </TableCell>
                                                ))}
                                                <TableCell>
                                                    {!Utils.isNullOrUndefined(product.salePackagingUnits) &&
                                                    product.salePackagingUnits.length > 0
                                                        ? product.salePackagingUnits[0].packaging
                                                        : ''}
                                                </TableCell>
                                                <TableCell>{getMinQuantity(product)}</TableCell>
                                                {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                                                    <TableCell>
                                                        {isLoaded ? (
                                                            `${getNetPriceWithPrecision(calculateUnitPrice(price))} ${getCurrencySymbol(product.saleCurrencyName)}`
                                                        ) : (
                                                            <Skeleton variant="text" width={60} />
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell>
                                                    <section className={styles.quantity_section}>
                                                        <QuantityInput
                                                            key={product.id}
                                                            quantity={quantities[product.id] ?? 1}
                                                            minQuantity={getMinQuantity(product)}
                                                            step={getOrderQuantity(product)}
                                                            warningMessage={
                                                                warnings[product.id]
                                                                    ? t(warnings[product.id])
                                                                    : undefined
                                                            }
                                                            onChange={(newQty) => handleQuantityChange(newQty, product)}
                                                        />
                                                    </section>
                                                </TableCell>
                                                {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) && (
                                                    <TableCell>
                                                        {isLoaded ? (
                                                            ` ${calculateTotalPricePerProduct(calculateUnitPrice(price), quantities[product.id] || 1, warnings[product.id].length > 0)} ${getCurrencySymbol(product.saleCurrencyName)}`
                                                        ) : (
                                                            <Skeleton variant="text" width={60} />
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell align="center">
                                                    {isLoaded ? (
                                                        <AddToShoppingCart
                                                            product={product}
                                                            minQuantity={getMinQuantity(product)}
                                                            quantity={quantities[product.id]}
                                                            productParentUuid={productId}
                                                            netPrice={calculateUnitPrice(price)}
                                                            brutPrice={price.priceFromPricing}
                                                            discount={price.discount}
                                                            familyCode={familyCode}
                                                            imageFileId={imageFileId}
                                                            packaging={
                                                                !Utils.isNullOrUndefined(product.salePackagingUnits) &&
                                                                product.salePackagingUnits.length > 0
                                                                    ? product.salePackagingUnits[0].packaging
                                                                    : null
                                                            }
                                                            isDisabled={warnings[product.id].length > 0 || !isLoaded}
                                                        ></AddToShoppingCart>
                                                    ) : (
                                                        <Skeleton variant="text" width={60} />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {expandedRows[product.id] &&
                                                product.attachments &&
                                                product.attachments?.length > 0 && (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={totalColumnCount}
                                                            sx={{
                                                                p: 0,
                                                                borderBottom: (theme) =>
                                                                    `1px solid ${theme.palette.divider}`,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    backgroundColor: '#fafafa',
                                                                    px: 3,
                                                                    py: 2,
                                                                }}
                                                            >
                                                                {product.attachments.map((attachment, index) => (
                                                                    <div
                                                                        className={styles.pdf_link}
                                                                        onClick={() => {
                                                                            getFileDocument(attachment.id);
                                                                        }}
                                                                        key={index}
                                                                    >
                                                                        {attachment.name}
                                                                    </div>
                                                                ))}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                        </React.Fragment>
                                    );
                                })}

                                {loading && (
                                    <TableRow>
                                        {[...Array(totalColumnCount)].map((_, index) => (
                                            <TableCell key={index}>
                                                <Skeleton variant="text" />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <div ref={loaderRef} style={{ height: '20px' }} />
                    </TableContainer>
                </Box>
            )}
        </div>
    );
}
